import React from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'

import { CheckRequiredFields } from '../../../../../components/CheckRequiredFields'
import { CheckFieldOptions } from '../../../../../components/CheckRequiredFields/getMissingFieldsFromData'
import { DeliveryForecastDespatchAdviceItem } from '../../../../../data/models'
import { calculateBreakpointWidth } from '../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../utils/wrapper'
import { useConfigContext } from '../../../../ui-config'
import { useDeliveryForecastDespatchAdviceById } from '../../../data'
import { validatePackagedQuantity } from '../../PackagedQuantityValidation/validatePackagedQuantity'
import { Overview } from './Overview'

type Props = {
  despatchAdviceId: string
  visible: boolean
  setVisible: React.Dispatch<boolean>
  handleClose: () => void
  isRequestRunning: boolean
  onSubmit: () => void
  isConfirmationActive: boolean
}

export const AcceptDeliveryForecastDespatchAdviceView: React.FC<Props> = ({
  despatchAdviceId,
  visible,
  setVisible,
  handleClose,
  isRequestRunning,
  onSubmit,
  isConfirmationActive,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()
  const { data: deliveryForecastDespatchAdvice } =
    useDeliveryForecastDespatchAdviceById(despatchAdviceId)

  const getAdditionalRequiredFieldPaths = (): CheckFieldOptions[] => {
    const additionalRequiredFieldPaths: CheckFieldOptions[] = []

    const items = deliveryForecastDespatchAdvice?.Payload.Items || []
    const calculateOpenQuantity = (
      item: DeliveryForecastDespatchAdviceItem,
    ) => {
      const openQuantity = item.OpenQuantity || item.Quantity || 0
      const confirmedQuantity = item.ConfirmationQuantity || 0

      return openQuantity - confirmedQuantity
    }

    items.forEach(item => {
      if (calculateOpenQuantity(item) > 0) {
        if (item.DeliveryStatus === 'NOT_SET')
          additionalRequiredFieldPaths.push({
            requiredFieldPath: 'articleDetail.deliveryStatus',
            orderPosition: item.OrderPosition,
          })
      }
    })

    return additionalRequiredFieldPaths
  }

  // ************************************************************
  // components
  // ************************************************************
  // deliveryNoteNumber
  const deliveryNoteNumberLabel = (
    <label htmlFor="deliveryNoteNumber" className="text-sm"></label>
  )

  const deliveryNoteNumberComponent = (
    <InputText
      id="deliveryNoteNumber"
      value={deliveryForecastDespatchAdvice?.Payload.DeliveryNoteNumber}
      className="w-full"
      disabled
    />
  )

  const deliveryNoteNumberWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .acceptDeliveryForecastDespatchAdvice
      }
      component={deliveryNoteNumberComponent}
      label={deliveryNoteNumberLabel}
      className="col-12 md:col-6 lg:col-4"
    />
  )

  // deliveryNoteDate
  const deliveryNoteDateLabel = (
    <label htmlFor="deliveryNoteDate" className="text-sm"></label>
  )

  const currentDate = new Date()

  const deliveryNoteDateComponent = (
    <Calendar
      id="deliveryNoteDate"
      value={currentDate}
      className="w-full"
      readOnlyInput
      locale={language}
      disabled
    />
  )

  const deliveryNoteDateWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .acceptDeliveryForecastDespatchAdvice
      }
      component={deliveryNoteDateComponent}
      label={deliveryNoteDateLabel}
      className="col-12 md:col-6 lg:col-4"
    />
  )

  // deliveryDate
  const deliveryDateLabel = (
    <label htmlFor="deliveryDate" className="text-sm"></label>
  )

  // const currentDate = new Date()

  const deliveryDateComponent = (
    <Calendar
      id="deliveryDate"
      value={
        new Date(
          deliveryForecastDespatchAdvice?.Payload.ConfirmationDeliveryDate!,
        )
      }
      className="w-full"
      readOnlyInput
      locale={language}
      disabled
    />
  )

  const deliveryDateWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .acceptDeliveryForecastDespatchAdvice
      }
      component={deliveryDateComponent}
      label={deliveryDateLabel}
      className="col-12 md:col-6 lg:col-4"
    />
  )

  // Additional Validation
  const packagedQuantityValidationResult = validatePackagedQuantity(
    deliveryForecastDespatchAdvice?.Payload,
  )
  const isDisabledByPackedQuantityValidation = !(
    packagedQuantityValidationResult?.isValid || false
  )

  // ************************************************************
  // elements
  // ************************************************************
  // dialog
  const header = (
    <>{t('deliveryForecastDespatchAdvice.title.send.dialog.title')}</>
  )

  const footer = (
    <>
      <Button
        label={t(
          'deliveryForecastDespatchAdvice.title.send.dialog.cancel.label',
        )}
        icon="pi pi-times"
        className="p-button-text w-full md:w-auto mb-1 md:mb-0"
        onClick={handleClose}
      />
      <Button
        label={t('deliveryForecastDespatchAdvice.title.send.dialog.send.label')}
        icon={'pi pi-check'}
        className="w-full md:w-auto"
        onClick={onSubmit}
        loading={isRequestRunning}
      />
    </>
  )

  return (
    <>
      <CheckRequiredFields
        data={deliveryForecastDespatchAdvice}
        additionalRequiredFieldPaths={getAdditionalRequiredFieldPaths()}
      >
        {(isDisabled, tooltip) => (
          <>
            {isConfirmationActive && tooltip}
            <Button
              label={t('deliveryForecastDespatchAdvice.title.send.label')}
              icon="pi pi-check"
              className="send-button"
              onClick={() => setVisible(true)}
              visible={
                config.deliveryForecastDespatchAdvice
                  .acceptDeliveryForecastDespatchAdvice.enabled !== false
              }
              disabled={
                isConfirmationActive
                  ? isDisabled || isDisabledByPackedQuantityValidation
                  : false
              }
            />
          </>
        )}
      </CheckRequiredFields>

      <Dialog
        visible={visible}
        header={header}
        style={{ width: '60rem' }}
        breakpoints={{ [calculateBreakpointWidth(60)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        <div className="formgrid grid">
          {/* deliveryNoteNumber */}
          {isConfirmationActive && deliveryNoteNumberWrapper}

          {/* deliveryNoteDate */}
          {deliveryNoteDateWrapper}

          {/* deliveryDate */}
          {deliveryDateWrapper}
        </div>

        <Overview despatchAdviceId={despatchAdviceId} />
      </Dialog>
    </>
  )
}
